import type { ReactNode } from "react";

import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";

import { DeselectBadge } from "./filter-chip";

export type NameSearchField = "firstName" | "lastName";

/** Base Latin alphabet (UK / international markets). */
export const LATIN_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
/** Danish alphabet — Latin plus Æ, Ø, Å. */
export const DANISH_ALPHABET = [...LATIN_ALPHABET, "Æ", "Ø", "Å"];

export type CollectNameFilterProps = {
  /** Which part of the name the selected letter applies to. */
  searchBy: NameSearchField;
  /** Caller must reset selectedLetter to null when searchBy changes. */
  onSearchByChange: (searchBy: NameSearchField) => void;
  selectedLetter: string | null;
  onSelectLetter: (letter: string | null) => void;
  /**
   * Letters to display. Defaults to the Latin alphabet; pass
   * {@link DANISH_ALPHABET} (or any locale-specific set) for markets that need
   * extra letters.
   */
  alphabet?: string[];
  /** Letters that have at least one match; others render faded. Omit to enable all. */
  availableLetters?: Set<string>;
  className?: string;
};

const NameTab = ({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "border-b-2 pb-0.5 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors",
      {
        "border-interface-neutral-6 text-interface-neutral-6": active,
        "border-transparent text-interface-neutral-4 hover:text-interface-neutral-5":
          !active
      }
    )}
  >
    {children}
  </button>
);

/**
 * First/last-name letter filter: a name toggle plus a letter grid. The
 * alphabet is localizable (Latin by default — pass {@link DANISH_ALPHABET} for
 * DK). Letters with no matches render faded; selecting the active letter clears
 * it.
 */
export const CollectNameFilter = ({
  searchBy,
  onSearchByChange,
  selectedLetter,
  onSelectLetter,
  alphabet = LATIN_ALPHABET,
  availableLetters,
  className
}: CollectNameFilterProps) => {
  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      <div className="flex items-center gap-4">
        <NameTab
          active={searchBy === "firstName"}
          onClick={() => {
            if (searchBy !== "firstName") onSearchByChange("firstName");
          }}
        >
          <T _str="First name" />
        </NameTab>
        <NameTab
          active={searchBy === "lastName"}
          onClick={() => {
            if (searchBy !== "lastName") onSearchByChange("lastName");
          }}
        >
          <T _str="Last name" />
        </NameTab>
      </div>

      <div className="grid grid-cols-6 gap-1.5">
        {alphabet.map((letter) => {
          const selected = selectedLetter === letter;
          const hasMatches = !availableLetters || availableLetters.has(letter);
          return (
            <button
              key={letter}
              type="button"
              onClick={() => {
                if (!hasMatches && !selected) return;
                onSelectLetter(selected ? null : letter);
              }}
              className={cn(
                "relative flex h-11 items-center justify-center rounded-lg text-base font-semibold transition-colors",
                {
                  "bg-interface-neutral-6 text-interface-neutral-0": selected,
                  "bg-interface-neutral-2 text-interface-neutral-6 hover:bg-interface-neutral-3":
                    !selected && hasMatches,
                  "bg-interface-neutral-2 text-interface-neutral-4 opacity-60 cursor-default":
                    !selected && !hasMatches
                }
              )}
            >
              {letter}
              {selected ? <DeselectBadge /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};
